import React from 'react';
import {
  EDIT_TABLE_COLUMNS_TYPE,
  WIDGET_GANTT_CHART_STATUS,
} from '@utils/enums';
import D_Number from '@utils/number';

const DATA_RANGE_ERROR_MESSAGE =
  'End Date should be greater than or equal to Start Date';

export const getGanttStructure = (edittingTableDataSource) => {
  const mapOfParentIndexAndParentBlock = new Map();
  const allParentIndexArray = [];

  edittingTableDataSource.forEach((x, index) => {
    if (x.isParent) {
      allParentIndexArray.push(index);
    }
  });
  allParentIndexArray.forEach((e, i, a) => {
    if (i === a.length - 1) {
      mapOfParentIndexAndParentBlock.set(e, edittingTableDataSource.slice(e));
    } else {
      mapOfParentIndexAndParentBlock.set(
        e,
        edittingTableDataSource.slice(e, a[i + 1]),
      );
    }
  });

  return {
    allParentIndexArray,
    mapOfParentIndexAndParentBlock,
  };
};

export const getOrgGridColumns = () => [
  {
    title: 'Hierarchy Structure',
    dataIndex: 'hierachy',
    required: false,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.OrgCombCell,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    required: true,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
  },
  {
    title: 'Type',
    dataIndex: 'type',
    required: true,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.SELECT,
    compProps: {
      options: [
        { label: 'Organization', value: 'Organization' },
        { label: 'Individual', value: 'Individual' },
      ],
      disabled: (record) => record.isTopNode === 1,
    },
  },
  {
    title: 'Title',
    dataIndex: 'title',
    required: (record) => record.type === 'Individual',
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
  },
];

export const getGanttGridColumns = (
  sourceData,
  edittingTableDataSource,
  isEditMode,
  isNotesHide,
  setIsNotesHide,
  isLightMode,
) => {
  const onClickHide = () => {
    sourceData.map((data) => {
      data.isnoteshide = !data.isnoteshide;
    });
    setIsNotesHide((v) => !v);
  };

  const getNotesTitle = (isEditMode, isNotesHide) => {
    if (isEditMode) {
      return (
        <a
          style={{ color: isLightMode ? 'black' : 'white' }}
          onClick={onClickHide}
        >
          Notes
          <span
            className={`hide-indicator appkiticon icon-view-fill ${
              isNotesHide ? 'lock' : 'unlock'
            }`}
          />
        </a>
      );
    }
    return (
      <div style={{ color: isLightMode ? 'black' : 'white' }}>
        Notes
        <span
          className={`hide-indicator appkiticon icon-view-fill ${
            isNotesHide ? 'lock' : 'unlock'
          }`}
        />
      </div>
    );
  };

  const dateRangeErrCheck = (type, startDate, endDate) => {
    let isErr = false;
    if (type !== 'Activity') {
      return isErr;
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      isErr = true;
    }
    return isErr;
  };

  const isSingleParent = (record) => {
    const recordIndex = edittingTableDataSource.findIndex(
      (x) => record.id === x.id,
    );
    const { mapOfParentIndexAndParentBlock } = getGanttStructure(
      edittingTableDataSource,
    );

    return mapOfParentIndexAndParentBlock.get(recordIndex)?.length === 1;
  };

  const result = [
    {
      title: 'Activity / Milestone Name',
      dataIndex: 'name',
      required: true,
      editable: true,
      type: EDIT_TABLE_COLUMNS_TYPE.GanttCombCell,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      required: true,
      editable: true,
      type: EDIT_TABLE_COLUMNS_TYPE.SELECT,
      compProps: {
        // disabled: (record) => {
        //   return record.isParent;
        // },
        options: [
          { label: 'Activity', value: 'Activity' },
          { label: 'Milestone', value: 'Milestone' },
        ],
        disabled: (record) =>
          record.isParent &&
          record.type === 'Activity' &&
          !isSingleParent(record),
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      required: true,
      editable: true,
      type: EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER,
      compProps: {
        disabled: (record) =>
          record.isParent &&
          record.type === 'Activity' &&
          !isSingleParent(record),
        rules: [
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (
                dateRangeErrCheck(
                  getFieldValue(
                    `type${_.field.substring(_.field.indexOf('_'))}`,
                  ),
                  getFieldValue(
                    `startDate${_.field.substring(_.field.indexOf('_'))}`,
                  )?.format(),
                  getFieldValue(
                    `endDate${_.field.substring(_.field.indexOf('_'))}`,
                  )?.format(),
                )
              ) {
                return Promise.reject(DATA_RANGE_ERROR_MESSAGE);
              }
              return Promise.resolve();
            },
          }),
        ],
      },
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      required: (record) => record.type === WIDGET_GANTT_CHART_STATUS.ACTIVITY,
      editable: true,
      type: EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER,
      compProps: {
        disabled: (record) =>
          record.type === 'Milestone' ||
          (record.isParent &&
            record.type === 'Activity' &&
            !isSingleParent(record)),
        rules: [
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (
                dateRangeErrCheck(
                  getFieldValue(
                    `type${_.field.substring(_.field.indexOf('_'))}`,
                  ),
                  getFieldValue(
                    `startDate${_.field.substring(_.field.indexOf('_'))}`,
                  )?.format(),
                  getFieldValue(
                    `endDate${_.field.substring(_.field.indexOf('_'))}`,
                  )?.format(),
                )
              ) {
                return Promise.reject(DATA_RANGE_ERROR_MESSAGE);
              }
              return Promise.resolve();
            },
          }),
        ],
      },
    },
    {
      title: '',
      dataIndex: 'duration',
      editable: true,
      required: true,
      type: EDIT_TABLE_COLUMNS_TYPE.GanttDurationCell,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      // required: true,
      editable: true,
      type: EDIT_TABLE_COLUMNS_TYPE.SELECT,
      compProps: {
        placement: 'topLeft',
        options: [
          'Not Started',
          'On Track',
          'At Risk',
          'Delayed',
          'Completed',
        ].map((e) => ({
          label: e,
          value: e,
        })),
      },
    },
  ];

  if (isEditMode || !isNotesHide) {
    const noteCol = {
      title: getNotesTitle(isEditMode, isNotesHide),
      dataIndex: 'notes',
      editable: true,
      type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
      // compProps: {
      //   rules: [
      //     ({ getFieldValue }) => ({
      //       validator(_, value) {
      //         if (value?.length > 500) {
      //           return Promise.reject('Character limit is 500.');
      //         }
      //         return Promise.resolve();
      //       },
      //     }),
      //   ],
      // },
    };
    result.push(noteCol);
  }

  return result;
};

export const WaterfallGridColumns = [
  {
    title: 'Name',
    dataIndex: 'l1',
    required: true,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
  },
  {
    title: 'Type',
    dataIndex: 'l2',
    required: true,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.SELECT,
    compProps: {
      options: [
        { label: 'Adjustment', value: 'Adjustment' },
        { label: 'Sum', value: 'Sum' },
      ],
      disabled: (record) => record.isFirst,
    },
  },
  {
    title: 'Value',
    dataIndex: 'value',
    required: (record) => record.l2 === 'Adjustment',
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
    compProps: {
      type: 'number',
      disabled: (record) => record.l2 === 'Sum',
      rules: [
        {
          required: true,
          message: `Value is required.`,
        },
      ],
      prefix: '$',
    },
    render: (text, record, index) => {
      if (record.l2 === 'Sum') {
        return D_Number.fixtoDollar(record.sumResult);
      }
      return D_Number.fixtoDollar(text);
    },
  },
  {
    title: 'Description',
    dataIndex: 'comment',
    required: false,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
    compProps: {
      rules: [
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (value?.length > 500) {
              return Promise.reject('Character limit is 500.');
            }
            return Promise.resolve();
          },
        }),
      ],
    },
  },
];

export const ChartWidgetOptions = [
  // { value: 'org', label: 'Org Chart', icon: 'icon-engagement-fill' },
  // { value: 'gantt', label: 'Gantt Chart', icon: 'icon-ganttchart-fill' },
  { value: 'spider', label: 'Spider Chart', icon: 'icon-spider-chart-fill' },
  // {
  //   value: 'waterfall',
  //   label: 'Waterfall Chart',
  //   icon: 'icon-bridgechart-outline',
  // },
  // {
  //   value: 'stackedBar',
  //   label: 'Stacked Bar Chart',
  //   icon: 'icon-bar-chart-outline',
  // },
];
